import { useEventListener } from "@vueuse/core";
import { computed, ref, unref } from "vue";
/**
 * Custom File-Drop composable
 * @param dropZone Element which files should be dropped on
 * @param onDrop callback function called when drop occurs
 * @param onDropCancel callback function called when drop cancelled
 * @param solo when true, only reacts if no modal is open
 * @param idleTime how long to wait until state resets
 */
export function useFileDrop(dropZone, onDrop, onDropCancel, solo, idleTime = 800) {
    /** returns if any bootstrap modal is open */
    function isAnyModalOpen() {
        return document.querySelectorAll(".modal.show").length > 0;
    }
    const currentState = ref("idle");
    let idleTimer = null;
    const resetTimer = () => {
        if (idleTimer) {
            clearTimeout(idleTimer);
        }
    };
    const stateMachine = {
        idle(event) {
            switch (event.type) {
                case "dragstart":
                    return "blocked";
                case "dragenter":
                    if (!(unref(solo) && isAnyModalOpen())) {
                        return "fileDragging";
                    }
                    break;
            }
            return "idle";
        },
        blocked(event) {
            switch (event.type) {
                case "drop":
                    return "idle";
                case "dragend":
                    return "idle";
            }
            return "blocked";
        },
        fileDragging(event) {
            resetTimer();
            switch (event.type) {
                case "dragover":
                    event.preventDefault();
                    idleTimer = setTimeout(() => (currentState.value = "idle"), idleTime);
                    break;
                case "drop":
                    event.preventDefault();
                    if (isFileOverDropZone.value) {
                        const dropHandler = unref(onDrop);
                        dropHandler(event);
                    }
                    else {
                        const dropCancelHandler = unref(onDropCancel);
                        dropCancelHandler(event);
                    }
                    return "idle";
                case "dragend":
                    return "idle";
            }
            return "fileDragging";
        },
    };
    const eventHandler = (event) => (currentState.value = stateMachine[currentState.value](event));
    useEventListener(document.body, "dragstart", eventHandler, true);
    useEventListener(document.body, "dragover", eventHandler, true);
    useEventListener(document.body, "drop", eventHandler, true);
    useEventListener(document.body, "dragend", eventHandler, true);
    useEventListener(document.body, "dragenter", eventHandler, true);
    const isFileOverDocument = computed({
        get() {
            return currentState.value === "fileDragging";
        },
        set(value) {
            if (value !== true) {
                currentState.value = "idle";
            }
            else {
                currentState.value = "fileDragging";
            }
        },
    });
    const isFileOverDropZone = ref(false);
    useEventListener(dropZone, "dragenter", () => {
        isFileOverDropZone.value = true;
    }, true);
    useEventListener(dropZone, "dragleave", () => {
        isFileOverDropZone.value = false;
    }, true);
    return { isFileOverDocument, isFileOverDropZone };
}
